/**
 * Domain constants and rules for Logic & Timing Reader Configuration.
 */

export const LOGIC_CONFIG_UNITS = {
  SECONDS: 'sec',
  MINUTES: 'min',
};

export const LOGIC_CONFIG_LIMITS = {
  MAX_SECONDS: 86400, // 24 hours limit in seconds
  MAX_MINUTES: 1440,  // 24 hours limit in minutes
  DEFAULT_MIN_SECONDS: 1,
  DEFAULT_MIN_MINUTES: 0,
};

/**
 * Sanitizes input string to allow strictly non-negative integer digits (0-9).
 * Eliminates negative signs, decimals, letters, spaces, and special symbols.
 */
export const sanitizeIntegerString = (rawInput) => {
  if (typeof rawInput !== 'string' && typeof rawInput !== 'number') {
    return '';
  }
  return String(rawInput).replace(/[^0-9]/g, '');
};

/**
 * Validates and clamps a numeric value within specified min/max domain boundaries.
 */
export const clampNumericValue = (val, min, max) => {
  let num = parseInt(val, 10);
  if (isNaN(num)) num = min;
  if (min !== undefined && num < min) num = min;
  if (max !== undefined && num > max) num = max;
  return num;
};

/**
 * Formats duration value into human-readable conversion label.
 */
export const formatUnitConversionLabel = (value, unit) => {
  const numValue = parseInt(value, 10) || 0;

  if (unit === LOGIC_CONFIG_UNITS.SECONDS) {
    if (numValue === 0) return '0s';
    if (numValue < 60) return `${numValue}s`;
    const mins = Math.floor(numValue / 60);
    const secs = numValue % 60;
    return secs === 0 ? `${mins} min` : `${mins} min ${secs}s`;
  }

  if (unit === LOGIC_CONFIG_UNITS.MINUTES) {
    if (numValue === 0) return 'deshabilitado';
    if (numValue < 60) return `${numValue} min`;
    const hours = Math.floor(numValue / 60);
    const mins = numValue % 60;
    return mins === 0 ? `${numValue} min (${hours}h)` : `${numValue} min (${hours}h ${mins}m)`;
  }

  return '';
};

/**
 * Declarative field schema for Logic Configuration fields.
 * Follows DRY and Open/Closed principle (easy to extend with new timing parameters).
 */
export const LOGIC_FIELDS_SCHEMA = [
  {
    key: 'default_auth_door_open_sec',
    label: 'Tiempo de Apertura (seg):',
    tooltip: 'Cuánto tiempo permanece desbloqueada la puerta tras un acceso válido.',
    unit: LOGIC_CONFIG_UNITS.SECONDS,
    min: 1,
    max: LOGIC_CONFIG_LIMITS.MAX_SECONDS,
    defaultValue: 5,
  },
  {
    key: 'door_open_grace_period_sec',
    label: 'Margen de Puerta Abierta (seg):',
    tooltip: 'Cuánto tiempo puede estar la puerta abierta antes de generar una alerta.',
    unit: LOGIC_CONFIG_UNITS.SECONDS,
    min: 1,
    max: LOGIC_CONFIG_LIMITS.MAX_SECONDS,
    defaultValue: 60,
  },
  {
    key: 'door_held_event_interval_sec',
    label: 'Frecuencia de Alerta (seg):',
    tooltip: 'Cada cuántos segundos se repite el aviso sonoro si la puerta sigue abierta.',
    unit: LOGIC_CONFIG_UNITS.SECONDS,
    min: 1,
    max: LOGIC_CONFIG_LIMITS.MAX_SECONDS,
    defaultValue: 5,
  },
  {
    key: 'alert_auto_silence_timeout_min',
    label: 'Apagado Automático (min):',
    tooltip: 'Aplica solo para emergencia, intrusión o puerta forzada. Apaga el sonido automáticamente, pero la lectora mantiene su estado de alerta.',
    unit: LOGIC_CONFIG_UNITS.MINUTES,
    min: 0,
    max: LOGIC_CONFIG_LIMITS.MAX_MINUTES,
    defaultValue: 5,
  },
];
